/**
 * Onboarding Wizard — Step-by-step setup for new users.
 *
 * A friendly wizard that guides non-technical users through initial setup:
 * 1. Welcome & language selection
 * 2. Theme selection (light/dark/system)
 * 3. Navigation mode (simple/advanced)
 * 4. Quick tour of main features
 *
 * Rendered as a modal overlay. Dismissed on completion or skip.
 */

import { html, nothing, type TemplateResult } from "lit";
import type { UiSettings } from "../storage.ts";
import type { ThemeMode } from "../theme.ts";
import type { Locale } from "../i18n.ts";

export type OnboardingStep = "welcome" | "theme" | "navmode" | "done";

const STEPS: OnboardingStep[] = ["welcome", "theme", "navmode", "done"];

type OnboardingCallbacks = {
  onLocaleChange: (locale: Locale) => void;
  onThemeChange: (theme: ThemeMode) => void;
  onNavModeChange: (mode: "simple" | "advanced") => void;
  onComplete: () => void;
  onSkip: () => void;
};

const STEP_CONTENT: Record<
  OnboardingStep,
  {
    title: Record<string, string>;
    description: Record<string, string>;
  }
> = {
  welcome: {
    title: {
      en: "Welcome to Nabe",
      th: "ยินดีต้อนรับสู่ Nabe",
      ja: "Nabeへようこそ",
      ko: "Nabe에 오신 것을 환영합니다",
      zh: "欢迎来到 Nabe",
    },
    description: {
      en: "Let's get you set up in just a few steps. Choose your preferred language to begin.",
      th: "มาตั้งค่าให้เสร็จใน 2-3 ขั้นตอน เลือกภาษาที่ต้องการเพื่อเริ่มต้น",
      ja: "数ステップでセットアップしましょう。お好みの言語を選択してください。",
      ko: "몇 단계로 설정을 완료하겠습니다. 원하는 언어를 선택하세요.",
      zh: "只需几步即可完成设置。选择您喜欢的语言开始。",
    },
  },
  theme: {
    title: {
      en: "Choose Your Look",
      th: "เลือกรูปแบบที่ชอบ",
      ja: "テーマを選択",
      ko: "테마 선택",
      zh: "选择外观",
    },
    description: {
      en: "Pick a visual theme that's comfortable for your eyes.",
      th: "เลือกธีมที่สบายตาที่สุด",
      ja: "目に優しいビジュアルテーマを選びましょう。",
      ko: "눈에 편한 테마를 선택하세요.",
      zh: "选择一个舒适的视觉主题。",
    },
  },
  navmode: {
    title: {
      en: "How Much Do You Want to See?",
      th: "อยากเห็นอะไรบ้าง?",
      ja: "表示モードを選択",
      ko: "표시 모드 선택",
      zh: "您想看到什么？",
    },
    description: {
      en: "Simple mode shows only essential features. Advanced mode shows everything.",
      th: "โหมดง่ายแสดงเฉพาะสิ่งจำเป็น โหมดขั้นสูงแสดงทุกอย่าง",
      ja: "シンプルモードは基本機能のみ、アドバンスモードはすべてを表示します。",
      ko: "심플 모드는 필수 기능만, 고급 모드는 모든 기능을 표시합니다.",
      zh: "简单模式只显示基本功能，高级模式显示全部功能。",
    },
  },
  done: {
    title: {
      en: "You're All Set!",
      th: "พร้อมแล้ว!",
      ja: "設定完了！",
      ko: "준비 완료!",
      zh: "设置完成！",
    },
    description: {
      en: "You can change these settings anytime from the Settings tab. Enjoy using Nabe!",
      th: "เปลี่ยนการตั้งค่าได้ทุกเมื่อจากแท็บตั้งค่า ขอให้สนุกกับ Nabe!",
      ja: "設定はいつでも変更できます。Nabeをお楽しみください！",
      ko: "설정은 언제든 변경할 수 있습니다. Nabe를 즐기세요!",
      zh: "您可以随时在设置中更改。享受使用 Nabe！",
    },
  },
};

function t(texts: Record<string, string>, locale: string): string {
  return texts[locale] ?? texts.en ?? "";
}

const LANGUAGES: Array<{ code: Locale; label: string; flag: string }> = [
  { code: "en", label: "English", flag: "🇬🇧" },
  { code: "th", label: "ไทย", flag: "🇹🇭" },
  { code: "ja", label: "日本語", flag: "🇯🇵" },
  { code: "ko", label: "한국어", flag: "🇰🇷" },
  { code: "zh", label: "中文", flag: "🇨🇳" },
];

function renderStep(
  step: OnboardingStep,
  settings: UiSettings,
  callbacks: OnboardingCallbacks,
): TemplateResult {
  const locale = settings.locale;
  const stepIdx = STEPS.indexOf(step);
  const isLast = step === "done";
  const isFirst = step === "welcome";

  const nextLabel: Record<string, string> = {
    en: "Next",
    th: "ถัดไป",
    ja: "次へ",
    ko: "다음",
    zh: "下一步",
  };
  const backLabel: Record<string, string> = {
    en: "Back",
    th: "กลับ",
    ja: "戻る",
    ko: "뒤로",
    zh: "返回",
  };
  const skipLabel: Record<string, string> = {
    en: "Skip Setup",
    th: "ข้ามการตั้งค่า",
    ja: "スキップ",
    ko: "건너뛰기",
    zh: "跳过设置",
  };
  const finishLabel: Record<string, string> = {
    en: "Get Started",
    th: "เริ่มใช้งาน",
    ja: "始める",
    ko: "시작하기",
    zh: "开始使用",
  };

  const stepContent = STEP_CONTENT[step];

  return html`
    <div class="onboarding-overlay">
      <div class="onboarding-card">
        <div class="onboarding-progress">
          ${STEPS.map(
            (s, i) => html`
            <div class="onboarding-dot ${i <= stepIdx ? "onboarding-dot--active" : ""}"></div>
          `,
          )}
        </div>

        <h2 class="onboarding-title">${t(stepContent.title, locale)}</h2>
        <p class="onboarding-desc">${t(stepContent.description, locale)}</p>

        <div class="onboarding-body">
          ${step === "welcome" ? renderLanguageStep(settings, callbacks) : nothing}
          ${step === "theme" ? renderThemeStep(settings, callbacks) : nothing}
          ${step === "navmode" ? renderNavModeStep(settings, callbacks) : nothing}
          ${step === "done" ? renderDoneStep(locale) : nothing}
        </div>

        <div class="onboarding-actions">
          ${
            !isFirst && !isLast
              ? html`<button class="onboarding-btn onboarding-btn--secondary"
                    data-action="back" data-step="${STEPS[stepIdx - 1]}"
                  >${t(backLabel, locale)}</button>`
              : nothing
          }
          ${
            isFirst
              ? html`<button class="onboarding-btn onboarding-btn--ghost"
                    @click=${callbacks.onSkip}
                  >${t(skipLabel, locale)}</button>`
              : nothing
          }
          ${
            isLast
              ? html`<button class="onboarding-btn onboarding-btn--primary"
                    @click=${callbacks.onComplete}
                  >${t(finishLabel, locale)}</button>`
              : html`<button class="onboarding-btn onboarding-btn--primary"
                    data-action="next" data-step="${STEPS[stepIdx + 1]}"
                  >${t(nextLabel, locale)}</button>`
          }
        </div>
      </div>
    </div>
  `;
}

function renderLanguageStep(settings: UiSettings, callbacks: OnboardingCallbacks): TemplateResult {
  return html`
    <div class="onboarding-options onboarding-options--lang">
      ${LANGUAGES.map(
        (lang) => html`
        <button
          class="onboarding-option ${settings.locale === lang.code ? "onboarding-option--selected" : ""}"
          @click=${() => callbacks.onLocaleChange(lang.code)}
        >
          <span class="onboarding-option__flag">${lang.flag}</span>
          <span class="onboarding-option__label">${lang.label}</span>
        </button>
      `,
      )}
    </div>
  `;
}

function renderThemeStep(settings: UiSettings, callbacks: OnboardingCallbacks): TemplateResult {
  const themes: Array<{ mode: ThemeMode; icon: string; labels: Record<string, string> }> = [
    {
      mode: "light",
      icon: "☀️",
      labels: { en: "Light", th: "สว่าง", ja: "ライト", ko: "라이트", zh: "浅色" },
    },
    {
      mode: "dark",
      icon: "🌙",
      labels: { en: "Dark", th: "มืด", ja: "ダーク", ko: "다크", zh: "深色" },
    },
    {
      mode: "system",
      icon: "💻",
      labels: { en: "System", th: "ตามระบบ", ja: "システム", ko: "시스템", zh: "跟随系统" },
    },
  ];

  return html`
    <div class="onboarding-options onboarding-options--theme">
      ${themes.map(
        (theme) => html`
        <button
          class="onboarding-option ${settings.theme === theme.mode ? "onboarding-option--selected" : ""}"
          @click=${() => callbacks.onThemeChange(theme.mode)}
        >
          <span class="onboarding-option__icon">${theme.icon}</span>
          <span class="onboarding-option__label">${t(theme.labels, settings.locale)}</span>
        </button>
      `,
      )}
    </div>
  `;
}

function renderNavModeStep(settings: UiSettings, callbacks: OnboardingCallbacks): TemplateResult {
  const modes: Array<{
    mode: "simple" | "advanced";
    icon: string;
    labels: Record<string, string>;
    desc: Record<string, string>;
  }> = [
    {
      mode: "simple",
      icon: "✨",
      labels: { en: "Simple", th: "ง่าย", ja: "シンプル", ko: "심플", zh: "简单" },
      desc: {
        en: "5 tabs, clean layout. Best for beginners.",
        th: "5 แท็บ เรียบง่าย เหมาะสำหรับผู้เริ่มต้น",
        ja: "5タブ、すっきりレイアウト。初心者向け。",
        ko: "5탭, 깔끔한 레이아웃. 초보자에게 추천.",
        zh: "5个标签，简洁布局。适合初学者。",
      },
    },
    {
      mode: "advanced",
      icon: "⚙️",
      labels: { en: "Advanced", th: "ขั้นสูง", ja: "アドバンス", ko: "고급", zh: "高级" },
      desc: {
        en: "All features visible. For power users.",
        th: "แสดงทุกฟีเจอร์ สำหรับผู้ใช้ขั้นสูง",
        ja: "全機能表示。パワーユーザー向け。",
        ko: "모든 기능 표시. 파워 유저용.",
        zh: "显示全部功能。适合高级用户。",
      },
    },
  ];

  return html`
    <div class="onboarding-options onboarding-options--navmode">
      ${modes.map(
        (m) => html`
        <button
          class="onboarding-option onboarding-option--wide ${settings.navMode === m.mode ? "onboarding-option--selected" : ""}"
          @click=${() => callbacks.onNavModeChange(m.mode)}
        >
          <span class="onboarding-option__icon">${m.icon}</span>
          <div>
            <span class="onboarding-option__label">${t(m.labels, settings.locale)}</span>
            <span class="onboarding-option__desc">${t(m.desc, settings.locale)}</span>
          </div>
        </button>
      `,
      )}
    </div>
  `;
}

function renderDoneStep(locale: string): TemplateResult {
  const tips: Record<string, string[]> = {
    en: [
      "Use the Chat tab to talk with your AI assistant",
      "Check Status to see system health",
      "Change settings anytime from the sidebar",
    ],
    th: ["ใช้แท็บแชทเพื่อคุยกับ AI ของคุณ", "ดูสถานะระบบได้ที่แท็บสถานะ", "เปลี่ยนการตั้งค่าได้ทุกเมื่อจากแถบด้านข้าง"],
    ja: [
      "チャットタブでAIアシスタントと会話",
      "ステータスでシステム状態を確認",
      "サイドバーからいつでも設定変更可能",
    ],
    ko: [
      "채팅 탭에서 AI 어시스턴트와 대화",
      "상태 탭에서 시스템 상태 확인",
      "사이드바에서 언제든 설정 변경 가능",
    ],
    zh: ["使用聊天标签与 AI 助手交流", "查看状态标签了解系统健康", "随时从侧边栏更改设置"],
  };

  const localeTips = tips[locale] ?? tips.en ?? [];
  return html`
    <div class="onboarding-tips">
      ${localeTips.map(
        (tip) => html`
        <div class="onboarding-tip">
          <span class="onboarding-tip__check">✓</span>
          <span>${tip}</span>
        </div>
      `,
      )}
    </div>
  `;
}

/**
 * Render the onboarding wizard overlay.
 * Returns nothing if onboarding is already completed.
 */
export function renderOnboarding(
  settings: UiSettings,
  currentStep: OnboardingStep,
  callbacks: OnboardingCallbacks,
): TemplateResult {
  if (settings.onboardingDone) return html`${nothing}`;
  return renderStep(currentStep, settings, callbacks);
}
